import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { songs } from "../songs";
import { Actions } from "../components/Actions";

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

function useSearch(term) {
  return useQuery({
    queryKey: ["search", term],
    queryFn: async ({ queryKey }) => {
      const [_, q] = queryKey;
      const url = `/api/search?query=${encodeURIComponent(q)}`;
      const res = await fetch(url, {headers: {"accept": "application/json"}});

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      return res.json();
    },
    enabled: term.length > 0
  });
}

function useSavedSongs(term) {
  return useQuery({
    queryKey: ["saved"],
    queryFn: async ({ queryKey }) => {
      const url = `/api/saved`;
      const res = await fetch(url, {headers: {"accept": "application/json"}});

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      return res.json();
    }
  });
}

export const Home = () => {
  document.title = "Lyrics";

  const [input, setInput] = useState("");
  const term = useDebouncedValue(input, 300);
  const searchQuery = useSearch(term);
  const savedSongsQuery = useSavedSongs(term);
  const [savedSongs, setSavedSongs] = useState([]);

  useEffect(() => {
    if (!savedSongsQuery.data) {
      return;
    }

    const list = savedSongsQuery.data.toSorted((a, b) => a.artist.localeCompare(b.artist, undefined, { sensitivity: "base" }) || a.title.localeCompare(b.title, undefined, { sensitivity: "base" }));
    setSavedSongs(list);
  }, [savedSongsQuery.data]);

  const handleRemove = async (id) => {
    if (!confirm("Remove this song?")) {
      return;
    }

    await fetch(`/api/remove/${id}`, {method: "delete", headers: {"accept": "application/json"}});
    savedSongsQuery.refetch();
  };

  return (
    <div>
      <h1 className="heading">Lyrics</h1>
      <p className="description">Because knowing the words actually matters!</p>

      <p className="search">
        <input type="search" placeholder="Search..." value={input} onChange={(e) => setInput(e.target.value)} />
      </p>

      {(searchQuery.isLoading || savedSongs.isLoading) && "Loading..."}

      <ol>
        {term && <>
          {!searchQuery.isLoading && !searchQuery.error && searchQuery.data && searchQuery.data.map((song) => (
              <li key={song.id}>
                <Link to={`/songs/${song.id}`}>
                  {song.artist} — {song.title}
                </Link>
              </li>
            ))}
        </>}

        {!term && savedSongs.map((song) => (
          <li key={song.id}>
            <Link to={`/songs/${song.id}`}>
              {song.artist} — {song.title}
            </Link>

            <button className="remove" onClick={() => handleRemove(song.id)}>×</button>
          </li>
        ))}
      </ol>

      <Actions />
    </div>
  );
};
